/**
 * The app's single source of truth for vault contents.
 *
 * Shape of the design:
 *
 *  - **Everything is loaded once.** The vault is small (hundreds of small text
 *    files) and we already had to read all of it to build the query index, so
 *    holding it in memory makes every widget read synchronous. No per-widget
 *    loading states, no waterfalls.
 *
 *  - **Writes are optimistic.** `mutate()` updates the visible content
 *    immediately and queues the intent. Ticking a checkbox must feel instant;
 *    it cannot wait on a round trip, least of all a git commit.
 *
 *  - **The queue holds intents, not text.** On a conflict we re-read the file
 *    and re-run the intent against fresh content. See `core/mutations.ts`.
 *
 *  - **The queue survives reloads.** It's mirrored to storage on every change,
 *    so closing the tab mid-flush doesn't lose the edit.
 */

import {
  applyMutation,
  coalesce,
  describeMutation,
  parseFile,
  VaultConflictError,
  type IndexEntry,
  type Mutation,
  type VaultFile,
  type VaultFS,
  type VaultIndex,
} from '@hud/core';

export type SyncStatus = 'idle' | 'pending' | 'saving' | 'error';

export interface OutboxStorage {
  read(): Mutation[];
  write(queue: Mutation[]): void;
}

/** Survives a page reload. Falls back to a no-op where storage is unavailable. */
export function localOutboxStorage(key = 'hud:outbox'): OutboxStorage {
  return {
    read() {
      try {
        const raw = globalThis.localStorage?.getItem(key);
        return raw ? (JSON.parse(raw) as Mutation[]) : [];
      } catch {
        return [];
      }
    },
    write(queue) {
      try {
        if (queue.length === 0) globalThis.localStorage?.removeItem(key);
        else globalThis.localStorage?.setItem(key, JSON.stringify(queue));
      } catch {
        // Private mode or quota exceeded — degrade to in-memory rather than break.
      }
    },
  };
}

export interface VaultStoreOptions {
  /** How long to wait for edits to settle before writing. */
  flushDelayMs?: number;
  storage?: OutboxStorage;
  maxRetries?: number;
}

const TEXT_EXTENSIONS = ['.md', '.css', '.json', '.txt', '.svg', '.yml', '.yaml'];

const isTextFile = (path: string): boolean =>
  TEXT_EXTENSIONS.some((extension) => path.endsWith(extension));

export class VaultStore {
  readonly #fs: VaultFS;
  readonly #storage: OutboxStorage;
  readonly #flushDelayMs: number;
  readonly #maxRetries: number;

  /** Last known server state: what we believe is committed. */
  readonly #base = new Map<string, VaultFile>();
  /** Unwritten intents, in order. */
  #queue: Mutation[] = [];

  /** Memoised optimistic content and parsed frontmatter, keyed by path. */
  readonly #viewCache = new Map<string, string>();
  readonly #entryCache = new Map<string, IndexEntry>();

  readonly #listeners = new Set<() => void>();
  #flushTimer: ReturnType<typeof setTimeout> | undefined;
  #flushing = false;

  status: SyncStatus = 'idle';
  error: string | null = null;

  constructor(fs: VaultFS, options: VaultStoreOptions = {}) {
    this.#fs = fs;
    this.#flushDelayMs = options.flushDelayMs ?? 1200;
    this.#maxRetries = options.maxRetries ?? 3;
    this.#storage = options.storage ?? localOutboxStorage();
    this.#queue = this.#storage.read();
  }

  // ─── Reading ──────────────────────────────────────────────────────────────

  /** Read every text file. Binary assets are served by URL instead. */
  async load(): Promise<void> {
    const entries = (await this.#fs.list()).filter((entry) => isTextFile(entry.path));
    const files = await Promise.all(
      entries.map(async (entry) => {
        try {
          return await this.#fs.read(entry.path);
        } catch {
          return null;
        }
      }),
    );

    this.#base.clear();
    for (const file of files) {
      if (file) this.#base.set(file.path, file);
    }

    this.#invalidateAll();
    this.status = this.#queue.length > 0 ? 'pending' : 'idle';
    this.#emit();

    // A queue restored from a previous session should go out immediately.
    if (this.#queue.length > 0) this.#scheduleFlush();
  }

  /** Optimistic content: committed state plus everything still queued. */
  get(path: string): string | undefined {
    const cached = this.#viewCache.get(path);
    if (cached !== undefined) return cached;

    const base = this.#base.get(path);
    const pending = this.#queue.filter((mutation) => mutation.path === path);
    if (!base && pending.length === 0) return undefined;

    let content = base?.content ?? '';
    for (const mutation of pending) {
      try {
        content = applyMutation(content, mutation);
      } catch {
        // A mutation that no longer applies is dropped from the view; the flush
        // will report it properly rather than blanking the widget now.
      }
    }

    this.#viewCache.set(path, content);
    return content;
  }

  has(path: string): boolean {
    return this.get(path) !== undefined;
  }

  paths(): string[] {
    return [...this.#base.keys()].sort();
  }

  /**
   * The query index over optimistic content.
   *
   * Entries are cached per path and invalidated individually, so typing in the
   * editor re-parses one file rather than the whole vault on every keystroke.
   */
  index(): VaultIndex {
    const entries: IndexEntry[] = [];

    for (const path of this.#base.keys()) {
      if (!path.endsWith('.md')) continue;

      let entry = this.#entryCache.get(path);
      if (!entry) {
        entry = { path, data: parseFile(this.get(path) ?? '').data };
        this.#entryCache.set(path, entry);
      }
      entries.push(entry);
    }

    return { entries };
  }

  // ─── Writing ──────────────────────────────────────────────────────────────

  /** Apply optimistically and queue for writing. */
  mutate(...mutations: Mutation[]): void {
    if (mutations.length === 0) return;

    this.#queue = coalesce([...this.#queue, ...mutations]);
    this.#storage.write(this.#queue);

    for (const mutation of mutations) this.#invalidate(mutation.path);

    this.status = 'saving';
    this.error = null;
    this.#emit();
    this.#scheduleFlush();
  }

  get pendingCount(): number {
    return this.#queue.length;
  }

  #scheduleFlush(): void {
    if (this.#flushTimer !== undefined) clearTimeout(this.#flushTimer);
    this.#flushTimer = setTimeout(() => void this.flush(), this.#flushDelayMs);
  }

  /** Write everything queued. Safe to call at any time. */
  async flush(): Promise<void> {
    if (this.#flushTimer !== undefined) {
      clearTimeout(this.#flushTimer);
      this.#flushTimer = undefined;
    }
    if (this.#flushing || this.#queue.length === 0) return;

    this.#flushing = true;
    this.status = 'saving';
    this.#emit();

    // Snapshot the queue: edits made during the flush stay queued for the next.
    const flushing = this.#queue;
    this.#queue = [];

    const byPath = new Map<string, Mutation[]>();
    for (const mutation of flushing) {
      const list = byPath.get(mutation.path);
      if (list) list.push(mutation);
      else byPath.set(mutation.path, [mutation]);
    }

    const failures: { mutations: Mutation[]; message: string }[] = [];

    await Promise.all(
      [...byPath.entries()].map(async ([path, mutations]) => {
        try {
          await this.#writePath(path, mutations);
        } catch (err) {
          failures.push({
            mutations,
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }),
    );

    if (failures.length > 0) {
      // Requeue so nothing is lost, and surface the first reason.
      this.#queue = [...failures.flatMap((failure) => failure.mutations), ...this.#queue];
      this.status = 'error';
      this.error = failures[0]!.message;
    } else {
      this.status = this.#queue.length > 0 ? 'saving' : 'idle';
      this.error = null;
    }

    this.#storage.write(this.#queue);
    this.#flushing = false;
    this.#emit();

    if (this.#queue.length > 0 && failures.length === 0) this.#scheduleFlush();
  }

  /**
   * Write one file, replaying the intents if the base moved under us.
   *
   * A `VaultConflictError` means someone else wrote first — another device, an
   * editor, an agent. Re-read, re-apply, retry. `MutationConflictError` means
   * the intent itself no longer makes sense (the task was deleted), which no
   * amount of retrying fixes, so it fails immediately.
   */
  async #writePath(path: string, mutations: Mutation[]): Promise<void> {
    for (let attempt = 0; attempt <= this.#maxRetries; attempt++) {
      const base = this.#base.get(path);
      let content = base?.content ?? '';

      for (const mutation of mutations) {
        content = applyMutation(content, mutation);
      }

      // A no-op write would still create a commit. Skip it.
      if (base && content === base.content) return;

      try {
        const { sha } = await this.#fs.write(path, content, base?.sha);
        this.#base.set(path, { path, content, sha });
        this.#invalidate(path);
        return;
      } catch (err) {
        if (!(err instanceof VaultConflictError) || attempt === this.#maxRetries) throw err;

        const fresh = await this.#fs.read(path);
        this.#base.set(path, fresh);
        this.#invalidate(path);
      }
    }
  }

  /** Commit message for a batch — surfaced by adapters that create commits. */
  static commitMessage(mutations: Mutation[]): string {
    if (mutations.length === 1) return describeMutation(mutations[0]!);
    return `Update ${new Set(mutations.map((m) => m.path)).size} file(s) from HUD`;
  }

  // ─── External changes ─────────────────────────────────────────────────────

  /**
   * Re-read one file after an outside edit.
   *
   * Only the base moves; queued intents are untouched and will replay on top,
   * so an edit you made in your editor and an edit you made in the HUD both
   * survive.
   */
  async refresh(path: string): Promise<void> {
    try {
      const file = await this.#fs.read(path);
      this.#base.set(path, file);
    } catch {
      this.#base.delete(path);
    }
    this.#invalidate(path);
    this.#emit();
  }

  // ─── Subscriptions ────────────────────────────────────────────────────────

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }

  #invalidate(path: string): void {
    this.#viewCache.delete(path);
    this.#entryCache.delete(path);
  }

  #invalidateAll(): void {
    this.#viewCache.clear();
    this.#entryCache.clear();
  }
}

export async function loadStore(fs: VaultFS, options?: VaultStoreOptions): Promise<VaultStore> {
  const store = new VaultStore(fs, options);
  await store.load();
  return store;
}
